import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogPosts } from "@/mocks/categories";

const guideImages: Record<string, string> = {
  "itaobuyspreadsheet-2026-guide": "https://readdy.ai/api/search-image?query=Clean%20workspace%20with%20laptop%20spreadsheet%20and%20fashion%20photos%2C%20dark%20walnut%20desk%2C%20natural%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-guide-jun27&orientation=landscape",
  "how-to-buy-from-china-safely": "https://readdy.ai/api/search-image?query=Shield%20security%20with%20shopping%20cart%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-safety-jun27&orientation=landscape",
  "agent-comparison-tips": "https://readdy.ai/api/search-image?query=Two%20shopping%20platforms%20comparison%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-compare-jun27&orientation=landscape",
  "itaobuyspreadsheet-vs-superbuy": "https://readdy.ai/api/search-image?query=Split%20screen%20spreadsheet%20versus%20platform%20comparison%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-vs-superbuy-jun27&orientation=landscape",
  "cheapest-way-buy-from-china": "https://readdy.ai/api/search-image?query=Money%20savings%20with%20shipping%20parcels%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-cheapest-jun27&orientation=landscape",
  "is-itaobuyspreadsheet-safe": "https://readdy.ai/api/search-image?query=Security%20shield%20checkmark%20with%20shopping%20elements%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-safe-jun27&orientation=landscape",
  "save-money-international-shipping": "https://readdy.ai/api/search-image?query=Shipping%20boxes%20with%20world%20map%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-shipping-jun27&orientation=landscape",
  "beginners-guide-taobao-spreadsheet": "https://readdy.ai/api/search-image?query=Hands%20holding%20smartphone%20shopping%20app%2C%20clean%20surface%2C%20daylight%2C%20beginner%20concept%2C%20no%20face%2C%20no%20text&width=600&height=400&seq=hub-beginner-jun27&orientation=landscape",
  "itaobuyspreadsheet-pricing": "https://readdy.ai/api/search-image?query=Price%20tags%20calculator%20with%20products%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-pricing-jun27&orientation=landscape",
  "top-products-itaobuyspreadsheet": "https://readdy.ai/api/search-image?query=Curated%20trending%20fashion%20products%20display%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-trending-jun27&orientation=landscape",
  "agent-verification-network": "https://readdy.ai/api/search-image?query=Network%20verification%20checkmarks%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-network-jun27&orientation=landscape",
  "spreadsheet-vs-agent-shopping": "https://readdy.ai/api/search-image?query=Split%20comparison%20spreadsheet%20versus%20agent%20concept%2C%20clean%20surface%2C%20daylight%2C%20editorial%2C%20no%20text&width=600&height=400&seq=hub-vs-agent-jun27&orientation=landscape",
};

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <section className="w-full pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-6xl mx-auto text-center">
            <span className="text-xs font-semibold text-secondary-600 uppercase tracking-widest">
              Knowledge Hub
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 mt-3 mb-4 leading-tight">
              ItaobuySpreadsheet
              <br />
              Shopping Guides
            </h1>
            <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              Complete guides covering everything from getting started with spreadsheet shopping to advanced agent comparison, safety tips, and cost-saving strategies for international buyers.
            </p>
          </div>
        </section>

        <section className="w-full py-12 md:py-20 bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {blogPosts.map((post, idx) => (
                <article
                  key={post.id}
                  className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all duration-300 animate-fade-in-up"
                  style={{ animationDelay: `${idx * 60}ms`, opacity: 0 }}
                >
                  <Link to={`/article/${post.slug}`} className="block cursor-pointer">
                    <div className="aspect-[3/2] w-full overflow-hidden">
                      <img
                        src={guideImages[post.id]}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  </Link>
                  <div className="p-5 md:p-6">
                    <Link to={`/article/${post.slug}`} className="block cursor-pointer">
                      <h2 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug mb-2">
                        {post.title}
                      </h2>
                    </Link>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                      {post.summary}
                    </p>
                    <Link
                      to={`/article/${post.slug}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-secondary-600 hover:text-secondary-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Read Full Guide
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform text-xs"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-14 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-foreground-950 hover:bg-foreground-800 text-background-50 font-semibold px-8 py-3.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "ItaobuySpreadsheet Shopping Guides",
            description: "Complete guides covering everything from getting started with spreadsheet shopping to advanced agent comparison, safety tips, and cost-saving strategies.",
            url: "https://itaobuy.cam/shopping-guide",
            image: "https://readdy.ai/api/search-image?query=Abstract%20geometric%20art%20composition%20with%20soft%20mustard%20yellow%20warm%20terracotta%20and%20olive%20green%20overlapping%20shapes%2C%20modern%20minimalist%20gradient%20background%20with%20subtle%20texture%20like%20handmade%20paper%2C%20clean%20contemporary%20aesthetic%2C%20no%20text%20no%20people%2C%20warm%20natural%20tones%2C%20high%20resolution%20editorial%20backdrop&width=1920&height=1080&seq=hero-bg-jun27-v2&orientation=landscape",
            isPartOf: {
              "@type": "WebSite",
              name: "itaobuy.cam",
              url: "https://itaobuy.cam/"
            },
            inLanguage: "en-US",
          }),
        }}
      />
    </>
  );
}